/**
 * Client-side image compression via canvas re-encoding.
 *
 * Opaque images -> JPEG at a fixed quality. Transparent images -> WebP,
 * also lossy, since it supports alpha - PNG (lossless, no quality/size
 * trade-off at all) was tried first for transparent images but confirmed
 * via testing to often make detailed, semi-transparent content LARGER,
 * not smaller (the browser's PNG encoder has no compression-level
 * control, unlike JPEG/WebP's quality parameter). WebP is not explicitly
 * listed as a supported format in Google's own CM360/DV360 HTML5 asset
 * docs (see compressImage's caller for the up-to-date UI labeling of
 * this trade-off) - callers should surface the output format clearly so
 * this is an informed choice, not a silent one.
 *
 * Longest side capped at MAX_DIMENSION - reference images for ad banners
 * rarely need to exceed this, and downscaling an oversized upload is
 * often the single biggest size win, independent of re-encode quality.
 */

const MAX_DIMENSION = 2000;
const JPEG_QUALITY = 0.8;
const WEBP_QUALITY = 0.8;

/** Formats that can never carry an alpha channel - skips the (more
 * expensive) pixel scan entirely for the common case of a JPEG source. */
const NEVER_TRANSPARENT_TYPES = new Set(["image/jpeg", "image/jpg"]);

/** Samples rather than scans every pixel - keeps this fast even on a
 * large image; a stride tuned to ~50,000 samples is plenty to detect
 * meaningful transparency (a cutout, a logo-like asset) without reading
 * every byte of a multi-megapixel image for a simple yes/no check. */
function detectTransparency(ctx, width, height) {
  const { data } = ctx.getImageData(0, 0, width, height);
  const totalPixels = width * height;
  const stride = Math.max(4, Math.floor(totalPixels / 50000) * 4);
  for (let i = 3; i < data.length; i += stride) {
    if (data[i] < 255) return true;
  }
  return false;
}

/**
 * Compresses an image file client-side. Returns a new File (WebP if the
 * source has transparency, JPEG otherwise) - never smaller in count than
 * the original in edge cases (e.g. an already-tiny, already-compressed
 * source), so callers should compare sizes and let the person decide
 * whether the result is actually worth using.
 */
export async function compressImage(file) {
  const bitmap = await createImageBitmap(file);

  let { width, height } = bitmap;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const couldHaveAlpha = !NEVER_TRANSPARENT_TYPES.has(file.type);
  const isTransparent = couldHaveAlpha && detectTransparency(ctx, width, height);

  const outputType = isTransparent ? "image/webp" : "image/jpeg";
  const outputExt = isTransparent ? "webp" : "jpg";
  const quality = isTransparent ? WEBP_QUALITY : JPEG_QUALITY;

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't compress this image."))), outputType, quality);
  });

  const baseName = file.name.replace(/\.[^/.]+$/, "");
  return new File([blob], `${baseName}.${outputExt}`, { type: outputType });
}